import type { SocialPostPreviewCardMetadata, SocialPostPreviewMedia } from '@wix/ai-scheduling-assistant-common-lib/entities';
import { formatSocialPostSchedule, type SocialPostRendition } from './social-post-preview-card';

export const POST_GRID_TILES = 4;
export const POST_TALL_FRAME_RATIO = 9 / 16;
export const POST_UNMEASURED_PHOTO = 1;
export const POST_UNMEASURED_VIDEO = 16 / 9;
export const POST_REEL_RENDITION: SocialPostRendition = { mode: 'fit', width: 564, height: 1002 };
export const POST_STORY_RENDITION: SocialPostRendition = { mode: 'fit', width: 600, height: 1066 };
const TALL_FRAME_TOLERANCE = 0.03;
const NOT_A_FEED_POST: readonly SocialPostPreviewCardMetadata['postType'][] = ['reel', 'story', 'short', 'pin'];

export type PostGridCount = 2 | 3 | 4;

export type PostMediaGrid =
  | { kind: 'none' }
  | { kind: 'single'; tile: SocialPostPreviewMedia }
  | { kind: 'grid'; count: PostGridCount; tiles: SocialPostPreviewMedia[]; hidden: number };

export type PostCaptionSegment = { text: string; kind: 'text' | 'tag' | 'url' };

export interface PostCaptionCut {
  limit: number;
}

export interface PostCaptionMarks {
  hashtags: boolean;
  mentions: boolean;
  urls: 'marked' | 'plain';
}

export interface PostTitleAndBody {
  title?: string;
  body?: string;
}

const RTL_LETTER = /[\p{Script=Hebrew}\p{Script=Arabic}\p{Script=Syriac}\p{Script=Thaana}\p{Script=Nko}\p{Script=Adlam}]/u;
const LETTER = /\p{L}/u;
const HASHTAG = String.raw`(?<![\p{L}\p{N}_])#[\p{L}\p{M}\p{N}_\u200C\u200D\u05BE\u05F3\u05F4]+`;

export const POST_PLAIN_MARKS: PostCaptionMarks = { hashtags: false, mentions: false, urls: 'plain' };
const MENTION = String.raw`(?<![\p{L}\p{N}_.@])@[A-Za-z0-9_](?:[A-Za-z0-9_.]*[A-Za-z0-9_])?`;
const WEB_ADDRESS = String.raw`(?:https?:\/\/(?:www\.)?|www\.)[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(?:\/(?!\/)[^\s.,]*(?:#[^\s.,]*)?)?(?=[.,\s]|$)`;

export function postAuthorName(card: Pick<SocialPostPreviewCardMetadata, 'accountName' | 'networkLabel'>): string {
  return card.accountName ?? card.networkLabel;
}

export function postInitial(name: string): string {
  return Array.from(name.trim())[0]?.toUpperCase() ?? '';
}

export function postScheduledLine(
  card: Pick<SocialPostPreviewCardMetadata, 'scheduledAt' | 'scheduledText' | 'labels'>,
  locale: string | undefined,
): string | undefined {
  const when = (card.scheduledAt && formatSocialPostSchedule(card.scheduledAt, locale)) || card.scheduledText;
  return when ? `${card.labels.scheduled} ${when}` : undefined;
}

export function postExpiryNote(card: Pick<SocialPostPreviewCardMetadata, 'expiresWithinDay' | 'labels'>): string | undefined {
  return card.expiresWithinDay && card.labels.expires ? card.labels.expires : undefined;
}

export function postIsFeedShaped(card: Pick<SocialPostPreviewCardMetadata, 'postType'>): boolean {
  return !NOT_A_FEED_POST.includes(card.postType);
}

export function postMediaKey(card: Pick<SocialPostPreviewCardMetadata, 'media'>): string {
  return card.media.map((item) => item.url).join(' ');
}

export function postCaptionText(caption: string, expanded: boolean, { limit }: PostCaptionCut): { text: string; truncated: boolean } {
  const codePoints = Array.from(caption);
  if (expanded || limit >= codePoints.length) {
    return { text: caption, truncated: false };
  }
  return { text: `${codePoints.slice(0, limit).join('')}...`, truncated: true };
}

function firstStrongDirection(text: string): 'rtl' | 'ltr' | undefined {
  for (const char of text) {
    if (LETTER.test(char)) {
      return RTL_LETTER.test(char) ? 'rtl' : 'ltr';
    }
  }
  return undefined;
}

export function postCaptionDirection(caption: string): 'rtl' | 'ltr' | undefined {
  const plain = postCaptionSegments(caption, { hashtags: true, mentions: true })
    .filter((segment) => segment.kind === 'text')
    .map((segment) => segment.text)
    .join('');
  return firstStrongDirection(plain) ?? firstStrongDirection(caption);
}

export function postCaptionSegments(
  text: string,
  { hashtags = true, mentions = false }: { hashtags?: boolean; mentions?: boolean } = {},
): PostCaptionSegment[] {
  const marks = new RegExp([WEB_ADDRESS, hashtags && HASHTAG, mentions && MENTION].filter(Boolean).join('|'), 'giu');
  const segments: PostCaptionSegment[] = [];
  let cursor = 0;
  for (const match of text.matchAll(marks)) {
    const start = match.index ?? 0;
    if (start > cursor) {
      segments.push({ text: text.slice(cursor, start), kind: 'text' });
    }
    const url = !match[0].startsWith('#') && !match[0].startsWith('@');
    segments.push({ text: match[0], kind: url ? 'url' : 'tag' });
    cursor = start + match[0].length;
  }
  if (cursor < text.length) {
    segments.push({ text: text.slice(cursor), kind: 'text' });
  }
  return segments;
}

export function postTitleAndBody(caption: string | undefined, titleLimit: number): PostTitleAndBody {
  const text = caption?.trim();
  if (!text) {
    return {};
  }
  const cut = text.indexOf('\n\n');
  const head = (cut < 0 ? text : text.slice(0, cut)).trim();
  const rest = cut < 0 ? undefined : text.slice(cut + 2).trim() || undefined;
  if (head && !head.includes('\n') && Array.from(head).length <= titleLimit) {
    return rest ? { title: head, body: rest } : { title: head };
  }
  return { body: text };
}

export function postMediaTotal(card: Pick<SocialPostPreviewCardMetadata, 'media' | 'mediaCount'>): number {
  return Math.max(card.mediaCount, card.media.length);
}

export function postMediaGrid(card: Pick<SocialPostPreviewCardMetadata, 'media' | 'mediaCount'>): PostMediaGrid {
  const { media } = card;
  if (media.length === 0) {
    return { kind: 'none' };
  }
  if (media.length === 1) {
    return { kind: 'single', tile: media[0] };
  }
  if (media.length < POST_GRID_TILES) {
    return { kind: 'grid', count: media.length === 2 ? 2 : 3, tiles: [...media], hidden: 0 };
  }
  const total = postMediaTotal(card);
  return { kind: 'grid', count: POST_GRID_TILES, tiles: media.slice(0, POST_GRID_TILES), hidden: total > POST_GRID_TILES ? total - POST_GRID_TILES + 1 : 0 };
}

export function postShowsNotShown(card: Pick<SocialPostPreviewCardMetadata, 'media' | 'mediaCount' | 'labels'>): boolean {
  return card.mediaCount > card.media.length && !!card.labels.mediaNotShown;
}

export function postGridShowsNotShown(card: Pick<SocialPostPreviewCardMetadata, 'media' | 'mediaCount' | 'labels'>): boolean {
  return card.media.length < POST_GRID_TILES && postShowsNotShown(card);
}

export function postLinkUrl(link: string | undefined): string | undefined {
  if (!link) {
    return undefined;
  }
  try {
    const { protocol, hostname } = new URL(link);
    return protocol === 'https:' && hostname ? link : undefined;
  } catch {
    return undefined;
  }
}

export function postLinkDomain(link: string, { keepWww = false }: { keepWww?: boolean } = {}): string | undefined {
  try {
    const { hostname } = new URL(link);
    return (keepWww ? hostname : hostname.replace(/^www\./i, '')) || undefined;
  } catch {
    return undefined;
  }
}

export function postGridRenditions(width: number, height: number): (count: PostGridCount, index: number) => SocialPostRendition {
  const tall: SocialPostRendition = { mode: 'fill', width, height: height * 2 };
  const quarter: SocialPostRendition = { mode: 'fill', width, height };
  return (count, index) => (count === 2 || (count === 3 && index === 0) ? tall : quarter);
}

export function postFillsTallFrame(width: number, height: number): boolean {
  return width > 0 && height > 0 && Math.abs(width / height - POST_TALL_FRAME_RATIO) <= TALL_FRAME_TOLERANCE;
}

export function postNaturalAspect(width: number, height: number): number | undefined {
  return width > 0 && height > 0 ? width / height : undefined;
}
